import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import { definePreset } from '@primevue/themes';
import { getDatePickerDateFormat } from '@peanuds/shared';

// COMPONENTS
import Toast from 'primevue/toast';
import ConfirmDialog from 'primevue/confirmdialog';
import Popover from 'primevue/popover';
import OverlayBadge from 'primevue/overlaybadge';
import Tooltip from 'primevue/tooltip';
import Select from 'primevue/select';
// THEMES
import Aura from '@primevue/themes/aura';

import type { Preset } from '@primeuix/themes/types';
import type { App, Plugin } from 'vue';

interface CreatePrimeVuePluginOptions {
  preset?: Preset;
  dateLocale?: Intl.LocalesArgument;
}

export const createPrimeVuePlugin = ({
  preset = BebawaPreset,
  dateLocale,
}: CreatePrimeVuePluginOptions = {}): Plugin => ({
  install(app: App) {
    app.use(PrimeVue, {
      ...(dateLocale ? { locale: { dateFormat: getDatePickerDateFormat(dateLocale) } } : {}),
      ripple: true,
      zIndex: {
        modal: 1100,
        overlay: 1000,
        menu: 1000,
        tooltip: 11000,
      },
      theme: {
        preset,
        options: {
          darkModeSelector: false, // TODO: Make dark mode work,
          cssLayer: true,
        },
      },
    });
    app.use(ToastService);
    app.use(ConfirmationService);
    // Register some components globally (using default names)
    app.component('Toast', Toast);
    app.component('ConfirmDialog', ConfirmDialog);
    app.component('Popover', Popover);
    app.component('OverlayBadge', OverlayBadge);
    // Avoid reserved HTML name; register as AppSelect
    app.component('AppSelect', Select);
    app.directive('tooltip', Tooltip);
  },
});

export const BebawaPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#E3F3FF',
      100: '#BCE4FF',
      200: '#94D4FF',
      300: '#6BC1FF',
      400: '#44AEFF',
      500: '#1B9BFF',
      600: '#1099FD',
      700: '#0C7ED7',
      800: '#0965B1',
      900: '#064C8B',
      950: '#042F5A',
    },
    secondary: {
      50: '{zinc.50}',
      100: '{zinc.100}',
      200: '{zinc.200}',
      300: '{zinc.300}',
      400: '{zinc.400}',
      500: '{zinc.500}',
      600: '{zinc.600}',
      700: '{zinc.700}',
      800: '{zinc.800}',
      900: '{zinc.900}',
      950: '{zinc.950}',
    },
    borderRadius: {
      m: '8px',
      lg: '12px',
      xl: '16px',
      xxl: '24px',
      absolut: '999px',
    },
  },
  components: {
    selectbutton: {
      root: { borderRadius: 'var(--p-border-radius-xxl)' },
    },
    togglebutton: {
      root: { borderRadius: 'var(--p-border-radius-xxl)' },
      content: { borderRadius: 'var(--p-border-radius-xxl)' },
    },
    menubar: {
      colorScheme: {
        baseItemBorderRadius: 'var(--p-border-radius-xxl)',
        root: {
          borderRadius: '14px',
        },
      },
    },
    card: {
      colorScheme: {
        root: {
          background: '#25242B',
          borderRadius: 'var(--p-border-radius-xxl)',
          color: 'white',
        },
        subtitleColor: 'white',
        captionGap: '3rem',
        bodyGap: '2rem',
        bodyPadding: '2.5rem',
      },
    },
    button: {
      colorScheme: {
        root: {
          secondaryBackground: '#F1F5F9',
          primaryBackground: '#1B9BFF',
        },
      },
    },
    listbox: {
      colorScheme: {
        optionBorderRadius: 'var(--p-border-radius-xxl)',
      },
    },
  },
});

export default createPrimeVuePlugin();
